'use client';

import React, { useState, useEffect } from 'react';
import { 
  TextInput, 
  Button, 
  Paper, 
  Title, 
  Stack, 
  Avatar, 
  Group,
  FileInput,
  Loader
} from '@mantine/core';
import { useAuth } from '../hooks/useAuth';
import { getUserProfile, updateUserProfile } from '../services/firestore';
import { UserProfile as UserProfileType } from '../types';

interface UserProfileProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ 
  onSuccess, 
  onError 
}) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Partial<UserProfileType>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      
      try {
        const userProfile = await getUserProfile(user.uid);
        if (userProfile) {
          setProfile(userProfile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        onError?.(error as Error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProfile();
  }, [user, onError]);
  
  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    
    try {
      // Handle photo upload if provided
      let photoURL = profile.photoURL;
      
      if (photoFile) {
        // In a real implementation, you would upload to Firebase Storage
        // and get the download URL
        // For now, we'll just simulate this
        photoURL = URL.createObjectURL(photoFile);
      }
      
      const updatedProfile = {
        ...profile,
        photoURL
      };
      
      await updateUserProfile(user.uid, updatedProfile);
      setProfile(updatedProfile);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating profile:', error);
      onError?.(error as Error);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Title order={2} mb="md">User Profile</Title>
      
      <Stack gap="md">
        <Group justify="center" mb="md">
          <Avatar 
            src={profile.photoURL} 
            size={120} 
            radius={120}
            alt={profile.displayName || 'User'}
          />
        </Group>
        
        <FileInput
          label="Profile Photo"
          placeholder="Upload a new photo"
          accept="image/png,image/jpeg"
          value={photoFile}
          onChange={setPhotoFile}
        />
        
        <TextInput
          label="Display Name"
          placeholder="Your name"
          value={profile.displayName || ''}
          onChange={(e) => setProfile({...profile, displayName: e.target.value})}
        />
        
        <TextInput
          label="Email"
          value={profile.email || ''}
          disabled
          description="Email cannot be changed directly"
        />
        
        <TextInput
          label="Phone Number"
          placeholder="Your phone number"
          value={profile.phoneNumber || ''}
          onChange={(e) => setProfile({...profile, phoneNumber: e.target.value})}
        />
        
        <TextInput
          label="Address"
          placeholder="Your address"
          value={profile.address || ''}
          onChange={(e) => setProfile({...profile, address: e.target.value})}
        />
        
        <TextInput
          label="Date of Birth"
          type="date"
          value={profile.dateOfBirth || ''}
          onChange={(e) => setProfile({...profile, dateOfBirth: e.target.value})}
        />
        
        <Button 
          onClick={handleSave} 
          loading={saving}
          fullWidth
        >
          Save Profile
        </Button>
      </Stack>
    </Paper>
  );
}; 